// The interactions previews the record overview reads (#12854, #12394).
//
// Two readers share each query, the Overview card and the page's own
// prefetch, and they must fetch identically or two fetchers race behind one
// key. The card lists the meetings the record took part in unless the org
// turns "Participant only" off for the record type: the same default the
// Interactions tab starts from (#9853). The card follows the org default
// only; the tab's per-record toggle is the user's choice for the tab.
//
// Upcoming meetings logged from a calendar (#12394, round 8) are read apart
// from the past interactions: the card groups them above the past rows and
// never counts them with them.
//
// The card also lists only the interaction types the record type's page layout
// names, when it names any (#14547). Both readers pass the same ids; the key
// carries them sorted, so their order in the layout never splits the cache.
//
// The tab-presence probe keeps its own unfiltered read under the bare
// ["rms", "overview-interactions", kind, id] key, so a record whose only
// meetings mention it, or whose only meeting is upcoming, still shows its
// Interactions tab.

import { fetchEntityInteractions } from "../api";
import { PREVIEW_LIMIT } from "../Overview/previewData";
import type { EntityInteractionsPage } from "../types";

/** How many upcoming rows the card shows before "N more upcoming". */
export const UPCOMING_SHOWN = 3;

/** More than two weeks of meetings with one record is not a case to page. */
const UPCOMING_FETCH_LIMIT = 50;

/** The chosen type ids in ascending order; none chosen is every type, `[]`. */
const sortedTypeIds = (typeIds: readonly number[] | null): readonly number[] =>
  [...(typeIds ?? [])].sort((a, b) => a - b);

export const overviewInteractionsQueryKey = (
  kind: string,
  id: string,
  participantOnly: boolean,
  typeIds: readonly number[] | null
) =>
  [
    "rms",
    "overview-interactions",
    kind,
    id,
    { participantOnly, typeIds: sortedTypeIds(typeIds) },
  ] as const;

export const fetchOverviewInteractions = (
  kind: string,
  id: string,
  participantOnly: boolean,
  typeIds: readonly number[] | null
): Promise<EntityInteractionsPage> =>
  fetchEntityInteractions(kind, id, {
    limit: PREVIEW_LIMIT + 1,
    participantOnly,
    typeIds: sortedTypeIds(typeIds),
    upcoming: false,
  });

// The upcoming group lists the same types as the past rows (#14547), so it
// takes the same ids, sorted the same way into its key.
export const overviewUpcomingQueryKey = (
  kind: string,
  id: string,
  participantOnly: boolean,
  typeIds: readonly number[] | null
) =>
  [
    "rms",
    "overview-interactions",
    kind,
    id,
    { participantOnly, typeIds: sortedTypeIds(typeIds), upcoming: true },
  ] as const;

export const fetchOverviewUpcoming = (
  kind: string,
  id: string,
  participantOnly: boolean,
  typeIds: readonly number[] | null
): Promise<EntityInteractionsPage> =>
  fetchEntityInteractions(kind, id, {
    limit: UPCOMING_FETCH_LIMIT,
    participantOnly,
    typeIds: sortedTypeIds(typeIds),
    upcoming: true,
  });
